import { apiFetch } from '../../utils/apiFetch';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ViewColumnsIcon, Squares2X2Icon, PlusIcon } from '@heroicons/react/24/outline';
import TenantSearchBar from './TenantSearchBar';
import TenantFilters from './TenantFilters';
import TenantTable from './TenantTable';
import TenantCard from './TenantCard';
import TenantCreationWizard from './TenantCreationWizard';
import BillingAdjustmentForm from './BillingAdjustmentForm';
import BulkOperationsPanel from './BulkOperationsPanel';
import TenantProfileView from './TenantProfileView';
import SubscriptionManagement from './SubscriptionManagement';
import TenantCommunication from './TenantCommunication';
import AccountMergeInterface from './AccountMergeInterface';
import TenantDeletionWorkflow from './TenantDeletionWorkflow';
import { useHybridAuth } from '../../contexts/HybridAuthContext';
import type { Tenant } from './TenantTable';

interface FilterState {
  status: string[];
  tier: string[];
  created_from: string;
  created_to: string;
}

interface SortConfig {
  key: keyof Tenant | 'first_name' | 'last_name' | 'role' | 'monthly_fee' | 'last_login_at';
  direction: 'asc' | 'desc';
}

const TenantManagement: React.FC = () => {
  const navigate = useNavigate();
  const { impersonate } = useHybridAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    tier: [],
    created_from: '',
    created_to: ''
  });
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'first_name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Modal states
  const [showCreationWizard, setShowCreationWizard] = useState(false);
  const [showBillingAdjustment, setShowBillingAdjustment] = useState(false);
  const [showBulkOperations, setShowBulkOperations] = useState(false);
  const [showTenantProfile, setShowTenantProfile] = useState(false);
  const [showSubscriptionManagement, setShowSubscriptionManagement] = useState(false);
  const [showTenantCommunication, setShowTenantCommunication] = useState(false);
  const [showAccountMerge, setShowAccountMerge] = useState(false);
  const [showTenantDeletion, setShowTenantDeletion] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, [searchQuery, filters, currentPage, sortConfig]);

  const fetchTenants = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Add search query
      if (searchQuery) params.append('search', searchQuery);
      
      // Add filters
      if (filters.status.length > 0) params.append('status', filters.status.join(','));
      if (filters.tier.length > 0) params.append('tier', filters.tier.join(','));
      if (filters.created_from) params.append('created_from', filters.created_from);
      if (filters.created_to) params.append('created_to', filters.created_to);
      
      // Add pagination
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());
      
      // Add sorting
      params.append('sort', sortConfig.key);
      params.append('direction', sortConfig.direction);
      
      const response = await apiFetch(`/api/admin/tenants?${params}`);
      if (!response.ok) throw new Error('Failed to fetch tenants');
      
      const data = await response.json();
      setTenants(data.tenants || data.active_tenants || []);
      setTotalItems(data.total || data.tenants?.length || 0);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      toast.error('Failed to load tenants');
    } finally {
      setIsLoading(false);
    }
  };

  // Event handlers
  const handleSelectTenant = (id: string) => {
    setSelectedTenants(prev => 
      prev.includes(id) 
        ? prev.filter(t => t !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (selected: boolean) => {
    setSelectedTenants(selected ? tenants.map(t => t.id) : []);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleFiltersChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      status: [],
      tier: [],
      created_from: '',
      created_to: ''
    });
    setCurrentPage(1);
  };

  const handleSort = (key: keyof Tenant | 'first_name' | 'last_name' | 'role' | 'monthly_fee' | 'last_login_at') => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleViewTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowTenantProfile(true);
  };

  const handleSuspendTenant = (tenant: Tenant) => {
    // For individual tenant suspension, we can use bulk operations with single tenant
    setSelectedTenants([tenant.id]);
    setShowBulkOperations(true);
  };

  const handleDeleteTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowTenantDeletion(true);
  };

  const handleManageSubscription = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowSubscriptionManagement(true);
  };

  const handleImpersonate = async (tenant: Tenant) => {
    if (confirm(`Impersonate ${tenant.first_name} ${tenant.last_name} (${tenant.email})?`)) {
      const success = await impersonate(tenant.id);
      if (success) {
        // Redirect to client dashboard
        navigate('/dashboard');
      }
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Additional handlers for new functionality
  const handleCreateTenant = () => {
    setShowCreationWizard(true);
  };

  const handleBillingAdjustment = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowBillingAdjustment(true);
  };

  const handleBulkOperations = () => {
    if (selectedTenants.length > 0) {
      setShowBulkOperations(true);
    }
  };

  const handleTenantCommunication = () => {
    if (selectedTenants.length > 0) {
      setShowTenantCommunication(true);
    }
  };

  const handleEditTenant = (tenant: Tenant) => {
    // For now, we'll just show a toast. In a real app, this would open an edit form
    toast.success(`Edit functionality for ${tenant.first_name} ${tenant.last_name} - to be implemented`);
  };

  // Modal handlers for wizard and forms
  const handleTenantCreated = async (tenantData: any) => {
    try {
      // In a real app, this would call the API to create the tenant
      console.log('Creating tenant:', tenantData);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      toast.success('Tenant created successfully!');
      setShowCreationWizard(false);
      fetchTenants(); // Refresh the list
    } catch (error) {
      toast.error('Failed to create tenant');
    }
  };

  const handleBillingAdjustmentSubmit = async (adjustment: any) => {
    try {
      // In a real app, this would call the API to process the billing adjustment
      console.log('Processing billing adjustment:', adjustment);
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
      toast.success('Billing adjustment processed successfully!');
      setShowBillingAdjustment(false);
      fetchTenants(); // Refresh the list
    } catch (error) {
      toast.error('Failed to process billing adjustment');
    }
  };

  const handleBulkOperationExecute = async (config: any) => {
    try {
      // In a real app, this would call the API to execute the bulk operation
      console.log('Executing bulk operation:', config);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      toast.success(`Bulk ${config.operation} operation completed successfully!`);
      setShowBulkOperations(false);
      setSelectedTenants([]); // Clear selection
      fetchTenants(); // Refresh the list
    } catch (error) {
      toast.error('Failed to execute bulk operation');
    }
  };

  const handleSubscriptionUpdate = async (tenantId: string, planId: string, billingCycle: string) => {
    try {
      // In a real app, this would call the API to update the subscription
      console.log('Updating subscription:', { tenantId, planId, billingCycle });
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      toast.success('Subscription updated successfully!');
      setShowSubscriptionManagement(false);
      fetchTenants(); // Refresh the list
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleCommunicationSend = async (message: any, tenantIds: string[]) => {
    try {
      // In a real app, this would call the API to send the communication
      console.log('Sending communication:', { message, tenantIds });
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      toast.success(`Message sent to ${tenantIds.length} tenant${tenantIds.length !== 1 ? 's' : ''}!`);
      setShowTenantCommunication(false);
    } catch (error) {
      toast.error('Failed to send communication');
    }
  };

  const handleAccountMerge = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowAccountMerge(true);
  };

  const handleMergeInitiate = async (mergePlan: any) => {
    try {
      // In a real app, this would call the API to initiate the merge
      console.log('Initiating account merge:', mergePlan);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      toast.success('Account merge initiated successfully!');
      setShowAccountMerge(false);
      fetchTenants(); // Refresh the list
    } catch (error) {
      toast.error('Failed to initiate account merge');
    }
  };

  const handleDeletionConfirm = async (deletionRequest: any) => {
    try {
      // In a real app, this would call the API to process the deletion
      console.log('Processing tenant deletion:', deletionRequest);
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      toast.success('Tenant deletion initiated successfully!');
      setShowTenantDeletion(false);
      fetchTenants(); // Refresh the list
    } catch (error) {
      toast.error('Failed to initiate tenant deletion');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Tenant Management</h2>
          <p className="text-sm text-slate-400 mt-1">
            Manage {totalItems.toLocaleString()} tenant{totalItems !== 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* View Mode Toggle */}
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 text-sm font-medium rounded-l-xl border transition-all duration-200 ${
                viewMode === 'table'
                  ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-300'
                  : 'bg-slate-700/50 border-slate-600/50 text-slate-300 hover:text-white hover:bg-slate-600/50'
              }`}
            >
              <ViewColumnsIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-2 text-sm font-medium rounded-r-xl border-l-0 border transition-all duration-200 ${
                viewMode === 'cards'
                  ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-300'
                  : 'bg-slate-700/50 border-slate-600/50 text-slate-300 hover:text-white hover:bg-slate-600/50'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
          </div>
          
          <button 
            className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-200"
            onClick={handleCreateTenant}
          >
            <PlusIcon className="h-4 w-4" />
            <span>Add Tenant</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <TenantSearchBar
        onSearch={handleSearch}
        onSelect={(tenant) => handleViewTenant(tenant)}
      />

      {/* Filters */}
      <TenantFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {/* Bulk Actions */}
      {selectedTenants.length > 0 && (
        <div className="bg-indigo-500/20 border border-indigo-500/30 rounded-xl p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="text-sm text-indigo-300">
              {selectedTenants.length} tenant{selectedTenants.length !== 1 ? 's' : ''} selected
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={handleBulkOperations}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-3 py-1 rounded-xl text-sm transition-colors duration-200"
              >
                Bulk Operations
              </button>
              <button 
                onClick={handleTenantCommunication}
                className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-1 rounded-xl text-sm transition-colors duration-200"
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {viewMode === 'table' ? (
        <TenantTable
          tenants={tenants}
          selectedTenants={selectedTenants}
          onSelectTenant={handleSelectTenant}
          onSelectAll={handleSelectAll}
          onViewTenant={handleViewTenant}
          onSuspendTenant={handleSuspendTenant}
          onDeleteTenant={handleDeleteTenant}
          onManageSubscription={handleManageSubscription}
          onImpersonate={handleImpersonate}
          isLoading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onSort={handleSort}
          sortConfig={sortConfig}
        />
      ) : (
        <div>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-slate-700/50 rounded-xl h-64 border border-slate-600/50"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tenants.map((tenant) => (
                  <TenantCard
                    key={tenant.id}
                    tenant={tenant}
                    onView={() => handleViewTenant(tenant)}
                    onSuspend={() => handleSuspendTenant(tenant)}
                    onDelete={() => handleDeleteTenant(tenant)}
                    onManageSubscription={() => handleManageSubscription(tenant)}
                    isSelected={selectedTenants.includes(tenant.id)}
                    onSelect={() => handleSelectTenant(tenant.id)}
                  />
                ))}
              </div>
              
              {/* Pagination for cards view */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-6">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-xl border border-slate-600/50 bg-slate-700/50 text-sm font-medium text-slate-300 hover:bg-slate-600/50 hover:text-white disabled:opacity-50 transition-all duration-200"
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all duration-200 ${
                            currentPage === page
                              ? 'z-10 bg-indigo-500/20 border-indigo-500/30 text-indigo-300'
                              : 'bg-slate-700/50 border-slate-600/50 text-slate-300 hover:bg-slate-600/50 hover:text-white'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-xl border border-slate-600/50 bg-slate-700/50 text-sm font-medium text-slate-300 hover:bg-slate-600/50 hover:text-white disabled:opacity-50 transition-all duration-200"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modals */}
      <TenantCreationWizard
        isOpen={showCreationWizard}
        onClose={() => setShowCreationWizard(false)}
        onComplete={handleTenantCreated}
      />

      <BillingAdjustmentForm
        isOpen={showBillingAdjustment}
        tenant={selectedTenant}
        onClose={() => setShowBillingAdjustment(false)}
        onSubmit={handleBillingAdjustmentSubmit}
      />

      <BulkOperationsPanel
        isOpen={showBulkOperations}
        selectedTenants={selectedTenants}
        tenants={tenants}
        onClose={() => setShowBulkOperations(false)}
        onExecute={handleBulkOperationExecute}
      />

      <TenantProfileView
        isOpen={showTenantProfile}
        tenant={selectedTenant}
        onClose={() => setShowTenantProfile(false)}
        onEdit={handleEditTenant}
        onSuspend={handleSuspendTenant}
        onDelete={handleDeleteTenant}
        onManageSubscription={handleManageSubscription}
        onBillingAdjustment={handleBillingAdjustment}
        onMergeTenant={handleAccountMerge}
      />

      <SubscriptionManagement
        isOpen={showSubscriptionManagement}
        tenant={selectedTenant}
        onClose={() => setShowSubscriptionManagement(false)}
        onUpdateSubscription={handleSubscriptionUpdate}
      />

      <TenantCommunication
        isOpen={showTenantCommunication}
        tenants={tenants}
        selectedTenants={selectedTenants}
        onClose={() => setShowTenantCommunication(false)}
        onSendMessage={handleCommunicationSend}
      />

      <AccountMergeInterface
        isOpen={showAccountMerge}
        sourceTenant={selectedTenant}
        onClose={() => setShowAccountMerge(false)}
        onInitiateMerge={handleMergeInitiate}
      />

      <TenantDeletionWorkflow
        isOpen={showTenantDeletion}
        tenant={selectedTenant}
        onClose={() => setShowTenantDeletion(false)}
        onConfirmDeletion={handleDeletionConfirm}
      />
    </div>
  );
};

export default TenantManagement;